import type { Language } from "../../../types/i18n";

// Part of the healthMore module (see ../healthMore.ts). English is the
// reference; every other language must carry the same keys.
// Medication screens: form, camera capture, card, list pages and the
// medication store's error messages. Dose units (mg, mL, IU ...) are medical
// units and stay as codes; only their spelled-out labels live here.
const en = {
  // MedicationForm
  medxFormTitleEdit: "Edit Medication",
  medxFormTitleNew: "New Medication",
  medxFormClose: "Close",
  medxFormSectionInfo: "Medication Information",
  medxFormName: "Medication Name (Trade Name)",
  medxFormNamePlaceholder: "e.g., Tylenol Extra Strength",
  medxFormIngredient: "Active Ingredient",
  medxFormIngredientPlaceholder: "e.g., Acetaminophen",
  medxFormManufacturer: "Manufacturer",
  medxFormManufacturerPlaceholder: "e.g., Bayer",
  medxFormPackageSize: "Package Size",
  medxFormSelect: "Select...",
  medxFormDoseForm: "Dosage Form",
  medxFormPurpose: "Prescription Purpose",
  medxFormStatus: "Status",
  medxFormSectionDosage: "Dosage",
  medxFormDose: "Dose",
  medxFormAmount: "Amount",
  medxFormUnit: "Unit",
  medxFormSelectUnit: "Select unit...",
  medxFormDosePreview: "Dose: {dose}",
  medxFormIntakeTimes: "Intake Times",
  medxFormInstructions: "Intake Instructions",
  medxFormInstructionsPlaceholder: "e.g., After meals with plenty of water",
  medxFormStartDate: "Start Date",
  medxFormEndDate: "End Date",
  medxFormSectionPrescription: "Prescription",
  medxFormPrescriber: "Prescriber Name",
  medxFormPrescriberPlaceholder: "e.g., Dr. Smith",
  medxFormCountry: "Prescription Country",
  medxFormSelectCountry: "Select a country...",
  medxFormReason: "Prescription Reason",
  medxFormReasonPlaceholder: "e.g., Acute treatment – headache",
  medxFormPrescriptionScan: "Digital Prescription or Scan",
  medxFormUploadPrescription: "Upload Prescription",
  medxFormPrescriptionUploaded: "Prescription uploaded",
  medxFormPrescriptionHint: "PDF or image (max. 5 MB)",
  medxFormNoteTitle: "Note:",
  medxFormSelfSignedNote:
    'When you enter a medication yourself, it is automatically marked as "Self Signed" and linked to your traveler account.',
  medxFormSectionImages: "Medication Images",
  medxFormImagesHint:
    "Upload images of the medication package and pills (optional)",
  medxFormPackageImage: "Package Image",
  medxFormPillImage: "Medication/Pill Image",
  medxFormPackagePreviewAlt: "Package preview",
  medxFormPillPreviewAlt: "Medication preview",
  medxFormRemoveImage: "Remove image",
  medxFormUploadPackage: "Upload Package Image",
  medxFormUploadPill: "Upload Medication Image",
  medxFormImageFormats: "JPG, PNG (max. 10 MB)",
  medxFormCaptureCamera: "Capture with Camera",
  medxFormImagesTip:
    "High-quality images help with identification and can be useful for travel documentation.",
  medxFormSaving: "Saving...",
  medxFormSaveChanges: "Save Changes",
  medxFormSaveNew: "Save Medication",
  medxFormFhirId: "FHIR Medication ID:",
  medxFormCapturePackageTitle: "Capture Package Image",
  medxFormCapturePillTitle: "Capture Medication Image",
  medxFormConfirmDeletePackage:
    "Do you really want to delete the package image?",
  medxFormConfirmDeletePill:
    "Do you really want to delete the medication image?",
  medxFormPackageDeleted: "Package image deleted",
  medxFormPillDeleted: "Medication image deleted",
  medxFormDeleteFailed: "Delete failed: {error}",
  medxFormPackageUploadFailed:
    "The medication was saved, but the package image upload failed.\nError: {error}",
  medxFormPillUploadFailed:
    "The medication was saved, but the medication image upload failed.\nError: {error}",
  medxFormSavedWithImages: "Medication and images saved successfully!",
  medxFormSaveFailed: "Failed to save medication",
  medxFormUnknownError: "Unknown error",

  // Dose forms (stored as codes)
  medxDoseFormTablet: "Tablet",
  medxDoseFormCapsule: "Capsule",
  medxDoseFormSyrup: "Syrup",
  medxDoseFormLiquid: "Liquid",
  medxDoseFormCream: "Cream",
  medxDoseFormInjection: "Injection",
  medxDoseFormInfusion: "Infusion",
  medxDoseFormSuppository: "Suppository",
  medxDoseFormInhaler: "Inhaler",

  // Package sizes (N1–N3 are German standard size codes and stay as-is)
  medxPackageSizeSample: "Sample",
  medxPackageSizeCustom: "Custom",

  // Statuses (stored as FHIR codes)
  medxStatusActive: "Active",
  medxStatusCompleted: "Completed",
  medxStatusStopped: "Stopped",
  medxStatusDraft: "Draft",
  medxStatusOnHold: "On Hold",
  medxStatusEnteredInError: "Entered in Error",
  medxStatusIntended: "Planned",
  medxStatusNotTaken: "Not Taken",
  medxStatusUnknown: "Unknown",

  // Dose unit groups and spelled-out unit names
  medxUnitGroupWeight: "Weight",
  medxUnitGroupVolume: "Volume",
  medxUnitGroupCount: "Count",
  medxUnitGroupUnits: "Units",
  medxUnitGroupPercentage: "Percentage",
  medxUnitMg: "milligrams",
  medxUnitG: "grams",
  medxUnitKg: "kilograms",
  medxUnitMl: "milliliters",
  medxUnitL: "liters",
  medxUnitMcg: "micrograms",
  medxUnitIu: "international units",
  medxUnitTablet: "Tablet(s)",
  medxUnitCapsule: "Capsule(s)",
  medxUnitDrops: "Drops",
  medxUnitPiece: "Piece(s)",
  medxDoseInputPlaceholder: "e.g., 500",

  // Intake timing (stored as boolean flags)
  medxTimingMorning: "Every morning",
  medxTimingNoon: "Every day at noon",
  medxTimingAfternoon: "Every afternoon",
  medxTimingEvening: "Every evening",
  medxTimingNight: "Every night",
  medxTimingBedtime: "At bedtime",

  // CameraCapture
  medxCamAccessDenied:
    "Camera access denied. Please check the permissions in your browser settings.",
  medxCamUnavailable: "Camera not available",
  medxCamCapturedAlt: "Captured photo",
  medxCamRetake: "Retake",
  medxCamUsePhoto: "Use Photo",
  medxCamSwitch: "Switch Camera",
  medxCamTake: "Take Photo",
  medxCamClose: "Close camera",

  // EnhancedMedicationCard
  medxCardExpiringSoon: "Expiring soon",
  medxCardExpired: "Expired",
  medxCardCategory: "Category",
  medxCardDosage: "Dosage",
  medxCardFrequency: "Frequency",
  medxCardStartDate: "Start date",
  medxCardInstructions: "Instructions:",
  medxCardBothPhotographed: "Package & medication photographed",
  medxCardPackagePhotographed: "Package photographed",
  medxCardPillPhotographed: "Medication photographed",
  medxCardEdit: "Edit medication",
  medxCardDelete: "Delete medication",
  medxCardPackageAlt: "Medication package",
  medxCardPillAlt: "Medication",

  // FHIRMedicationsPage
  medxPageLoading: "Loading medications...",
  medxPageSubtitle: "Manage your medications and prescriptions",
  medxPageTotal: "Total Medications",
  medxPageActive: "Active",
  medxPageExpiringSoon: "Expiring Soon",
  medxPageExpired: "Expired",
  medxPageSearch: "Search medications...",
  medxPageAllStatuses: "All statuses",
  medxPageAllCategories: "All categories",
  medxPageListTitle: "Medications ({count})",
  medxPageEmpty: "No medications found",
  medxPageEmptyFiltered: "Try adjusting your filters",
  medxPageEmptyStart: "Add your first medication to get started",
  medxPageAddFirst: "Add Your First Medication",
  medxPageConfirmDelete: "Are you sure you want to delete this medication?",
  medxPageDeleted: "Medication deleted",

  // MedicationAddPage / MedicationEditPage
  medxBack: "Back to medications",
  medxSavedToast: "Medication saved successfully!",
  medxUpdatedToast: "Medication updated successfully!",

  // MedicationsPage (legacy list)
  medxListSubtitle: "Manage your travel medications",
  medxListTotal: "Total",
  medxListCompleted: "Completed",
  medxListWithPrescription: "With prescription",
  medxListMine: "My Medications",
  medxListEmpty: "No medications yet",
  medxListEmptyHint: "Add your first medication",
  medxListManufacturer: "Manufacturer: {name}",
  medxListStart: "Start: {date}",
  medxListPrescriptionFrom: "Prescription from: {country}",

  // AddMedicationModal (legacy)
  medxModalDosagePlaceholder: "e.g., 400 mg",
  medxModalFrequencyPlaceholder: "e.g., twice daily",
  medxModalType: "Medication Type",
  medxModalTravelCategory: "Travel Category",
  medxModalExtendedTitle: "FHIR R5 Extended Data",
  medxModalTradeNamePlaceholder: "e.g., Advil, Tylenol",
  medxModalTradeNameHint: "Trade or brand name",
  medxModalIngredientPlaceholder: "e.g., Ibuprofen, Acetaminophen",
  medxModalIngredientHint: "Active ingredient (INN)",
  medxModalManufacturerPlaceholder: "e.g., Bayer, Pfizer",
  medxModalManufacturerHint: "Manufacturer of the medication",
  medxModalCodePlaceholder: "e.g., 12345678 or N02BA01",
  medxModalCodeHint: "International product code",
  medxModalBasicData: "Basic Information",
  medxModalPeriod: "Period",
  medxModalMedicalInfo: "Medical Information",
  medxModalCategory: "Category",
  medxModalPackagePhoto: "Package photo",
  medxModalPackagePhotoHint: "Photo of the medication package",
  medxModalPillPhoto: "Tablet/pill photo",
  medxModalPillPhotoHint: "Photo of the tablet/pill",

  // fhirMedicationStore error messages
  medxErrLoad: "Failed to load medications",
  medxErrLoadActive: "Failed to load active medications",
  medxErrLoadByCategory: "Failed to load medications by category",
  medxErrLoadExpiring: "Failed to load expiring medications",
  medxErrLoadOne: "Failed to load the medication",
  medxErrLoadStats: "Failed to load statistics",
  medxErrLoadTerminologies: "Failed to load terminologies",
  medxErrCreate: "Failed to create the medication",
  medxErrUpdate: "Failed to update the medication",
  medxErrDelete: "Failed to delete the medication",
};

const de: typeof en = {
  medxFormTitleEdit: "Medikament bearbeiten",
  medxFormTitleNew: "Neues Medikament",
  medxFormClose: "Schließen",
  medxFormSectionInfo: "Medikamenteninformationen",
  medxFormName: "Medikamentenname (Handelsname)",
  medxFormNamePlaceholder: "z. B. Aspirin Complex",
  medxFormIngredient: "Wirkstoff",
  medxFormIngredientPlaceholder: "z. B. Acetylsalicylsäure",
  medxFormManufacturer: "Hersteller",
  medxFormManufacturerPlaceholder: "z. B. Bayer",
  medxFormPackageSize: "Packungsgröße",
  medxFormSelect: "Bitte wählen...",
  medxFormDoseForm: "Darreichungsform",
  medxFormPurpose: "Verordnungszweck",
  medxFormStatus: "Status",
  medxFormSectionDosage: "Dosierung",
  medxFormDose: "Dosis",
  medxFormAmount: "Menge",
  medxFormUnit: "Einheit",
  medxFormSelectUnit: "Einheit wählen...",
  medxFormDosePreview: "Dosis: {dose}",
  medxFormIntakeTimes: "Einnahmezeiten",
  medxFormInstructions: "Einnahmehinweise",
  medxFormInstructionsPlaceholder: "z. B. Nach dem Essen mit viel Wasser",
  medxFormStartDate: "Beginn",
  medxFormEndDate: "Ende",
  medxFormSectionPrescription: "Verordnung",
  medxFormPrescriber: "Name des Verordners",
  medxFormPrescriberPlaceholder: "z. B. Dr. Müller",
  medxFormCountry: "Land der Verordnung",
  medxFormSelectCountry: "Land wählen...",
  medxFormReason: "Grund der Verordnung",
  medxFormReasonPlaceholder: "z. B. Akutbehandlung – Kopfschmerzen",
  medxFormPrescriptionScan: "Digitales Rezept oder Scan",
  medxFormUploadPrescription: "Rezept hochladen",
  medxFormPrescriptionUploaded: "Rezept hochgeladen",
  medxFormPrescriptionHint: "PDF oder Bild (max. 5 MB)",
  medxFormNoteTitle: "Hinweis:",
  medxFormSelfSignedNote:
    "Wenn Sie ein Medikament selbst eintragen, wird es automatisch als „Selbst signiert“ markiert und mit Ihrem Reisendenkonto verknüpft.",
  medxFormSectionImages: "Medikamentenbilder",
  medxFormImagesHint:
    "Laden Sie Bilder der Medikamentenpackung und der Tabletten hoch (optional)",
  medxFormPackageImage: "Packungsbild",
  medxFormPillImage: "Bild des Medikaments/der Tablette",
  medxFormPackagePreviewAlt: "Vorschau der Packung",
  medxFormPillPreviewAlt: "Vorschau des Medikaments",
  medxFormRemoveImage: "Bild entfernen",
  medxFormUploadPackage: "Packungsbild hochladen",
  medxFormUploadPill: "Medikamentenbild hochladen",
  medxFormImageFormats: "JPG, PNG (max. 10 MB)",
  medxFormCaptureCamera: "Mit Kamera aufnehmen",
  medxFormImagesTip:
    "Gute Bilder erleichtern die Identifizierung und können für Reisedokumente nützlich sein.",
  medxFormSaving: "Wird gespeichert...",
  medxFormSaveChanges: "Änderungen speichern",
  medxFormSaveNew: "Medikament speichern",
  medxFormFhirId: "FHIR-Medikamenten-ID:",
  medxFormCapturePackageTitle: "Packungsbild aufnehmen",
  medxFormCapturePillTitle: "Medikamentenbild aufnehmen",
  medxFormConfirmDeletePackage:
    "Möchten Sie das Packungsbild wirklich löschen?",
  medxFormConfirmDeletePill:
    "Möchten Sie das Medikamentenbild wirklich löschen?",
  medxFormPackageDeleted: "Packungsbild gelöscht",
  medxFormPillDeleted: "Medikamentenbild gelöscht",
  medxFormDeleteFailed: "Löschen fehlgeschlagen: {error}",
  medxFormPackageUploadFailed:
    "Das Medikament wurde gespeichert, aber das Hochladen des Packungsbilds ist fehlgeschlagen.\nFehler: {error}",
  medxFormPillUploadFailed:
    "Das Medikament wurde gespeichert, aber das Hochladen des Medikamentenbilds ist fehlgeschlagen.\nFehler: {error}",
  medxFormSavedWithImages: "Medikament und Bilder erfolgreich gespeichert!",
  medxFormSaveFailed: "Medikament konnte nicht gespeichert werden",
  medxFormUnknownError: "Unbekannter Fehler",

  medxDoseFormTablet: "Tablette",
  medxDoseFormCapsule: "Kapsel",
  medxDoseFormSyrup: "Sirup",
  medxDoseFormLiquid: "Flüssigkeit",
  medxDoseFormCream: "Creme",
  medxDoseFormInjection: "Injektion",
  medxDoseFormInfusion: "Infusion",
  medxDoseFormSuppository: "Zäpfchen",
  medxDoseFormInhaler: "Inhalator",

  medxPackageSizeSample: "Muster",
  medxPackageSizeCustom: "Individuell",

  medxStatusActive: "Aktiv",
  medxStatusCompleted: "Abgeschlossen",
  medxStatusStopped: "Abgesetzt",
  medxStatusDraft: "Entwurf",
  medxStatusOnHold: "Pausiert",
  medxStatusEnteredInError: "Fehlerhafte Eingabe",
  medxStatusIntended: "Geplant",
  medxStatusNotTaken: "Nicht eingenommen",
  medxStatusUnknown: "Unbekannt",

  medxUnitGroupWeight: "Gewicht",
  medxUnitGroupVolume: "Volumen",
  medxUnitGroupCount: "Stückzahl",
  medxUnitGroupUnits: "Einheiten",
  medxUnitGroupPercentage: "Prozent",
  medxUnitMg: "Milligramm",
  medxUnitG: "Gramm",
  medxUnitKg: "Kilogramm",
  medxUnitMl: "Milliliter",
  medxUnitL: "Liter",
  medxUnitMcg: "Mikrogramm",
  medxUnitIu: "Internationale Einheiten",
  medxUnitTablet: "Tablette(n)",
  medxUnitCapsule: "Kapsel(n)",
  medxUnitDrops: "Tropfen",
  medxUnitPiece: "Stück",
  medxDoseInputPlaceholder: "z. B. 500",

  medxTimingMorning: "Täglich morgens",
  medxTimingNoon: "Täglich mittags",
  medxTimingAfternoon: "Täglich nachmittags",
  medxTimingEvening: "Täglich abends",
  medxTimingNight: "Täglich nachts",
  medxTimingBedtime: "Vor dem Schlafengehen",

  medxCamAccessDenied:
    "Kamerazugriff verweigert. Bitte prüfen Sie die Berechtigungen in den Browser-Einstellungen.",
  medxCamUnavailable: "Kamera nicht verfügbar",
  medxCamCapturedAlt: "Aufgenommenes Foto",
  medxCamRetake: "Neu aufnehmen",
  medxCamUsePhoto: "Foto verwenden",
  medxCamSwitch: "Kamera wechseln",
  medxCamTake: "Foto aufnehmen",
  medxCamClose: "Kamera schließen",

  medxCardExpiringSoon: "Läuft bald ab",
  medxCardExpired: "Abgelaufen",
  medxCardCategory: "Kategorie",
  medxCardDosage: "Dosierung",
  medxCardFrequency: "Häufigkeit",
  medxCardStartDate: "Beginn",
  medxCardInstructions: "Hinweise:",
  medxCardBothPhotographed: "Packung & Medikament fotografiert",
  medxCardPackagePhotographed: "Packung fotografiert",
  medxCardPillPhotographed: "Medikament fotografiert",
  medxCardEdit: "Medikament bearbeiten",
  medxCardDelete: "Medikament löschen",
  medxCardPackageAlt: "Medikamentenpackung",
  medxCardPillAlt: "Medikament",

  medxPageLoading: "Medikamente werden geladen...",
  medxPageSubtitle: "Verwalten Sie Ihre Medikamente und Rezepte",
  medxPageTotal: "Medikamente gesamt",
  medxPageActive: "Aktiv",
  medxPageExpiringSoon: "Laufen bald ab",
  medxPageExpired: "Abgelaufen",
  medxPageSearch: "Medikamente suchen...",
  medxPageAllStatuses: "Alle Status",
  medxPageAllCategories: "Alle Kategorien",
  medxPageListTitle: "Medikamente ({count})",
  medxPageEmpty: "Keine Medikamente gefunden",
  medxPageEmptyFiltered: "Passen Sie Ihre Filter an",
  medxPageEmptyStart: "Fügen Sie Ihr erstes Medikament hinzu",
  medxPageAddFirst: "Erstes Medikament hinzufügen",
  medxPageConfirmDelete: "Möchten Sie dieses Medikament wirklich löschen?",
  medxPageDeleted: "Medikament gelöscht",

  medxBack: "Zurück zu den Medikamenten",
  medxSavedToast: "Medikament erfolgreich gespeichert!",
  medxUpdatedToast: "Medikament erfolgreich aktualisiert!",

  medxListSubtitle: "Verwalten Sie Ihre Reisemedikamente",
  medxListTotal: "Gesamt",
  medxListCompleted: "Abgeschlossen",
  medxListWithPrescription: "Mit Rezept",
  medxListMine: "Meine Medikamente",
  medxListEmpty: "Noch keine Medikamente",
  medxListEmptyHint: "Fügen Sie Ihr erstes Medikament hinzu",
  medxListManufacturer: "Hersteller: {name}",
  medxListStart: "Beginn: {date}",
  medxListPrescriptionFrom: "Verordnet in: {country}",

  medxModalDosagePlaceholder: "z. B. 400 mg",
  medxModalFrequencyPlaceholder: "z. B. 2x täglich",
  medxModalType: "Medikamentenart",
  medxModalTravelCategory: "Reisekategorie",
  medxModalExtendedTitle: "Erweiterte FHIR-R5-Daten",
  medxModalTradeNamePlaceholder: "z. B. Aspirin, Ibuprofen",
  medxModalTradeNameHint: "Handelsname/Markenname",
  medxModalIngredientPlaceholder: "z. B. Acetylsalicylsäure, Ibuprofen",
  medxModalIngredientHint: "Wirkstoff (INN)",
  medxModalManufacturerPlaceholder: "z. B. Bayer AG, Ratiopharm",
  medxModalManufacturerHint: "Hersteller des Medikaments",
  medxModalCodePlaceholder: "z. B. 12345678 oder N02BA01",
  medxModalCodeHint: "Internationaler Produktcode",
  medxModalBasicData: "Grunddaten",
  medxModalPeriod: "Zeitraum",
  medxModalMedicalInfo: "Medizinische Informationen",
  medxModalCategory: "Kategorie",
  medxModalPackagePhoto: "Packungsfoto",
  medxModalPackagePhotoHint: "Foto der Medikamentenpackung",
  medxModalPillPhoto: "Tabletten-/Pillenfoto",
  medxModalPillPhotoHint: "Foto der Tablette/Pille",

  medxErrLoad: "Fehler beim Laden der Medikamente",
  medxErrLoadActive: "Fehler beim Laden der aktiven Medikamente",
  medxErrLoadByCategory: "Fehler beim Laden der Medikamente nach Kategorie",
  medxErrLoadExpiring: "Fehler beim Laden ablaufender Medikamente",
  medxErrLoadOne: "Fehler beim Laden des Medikaments",
  medxErrLoadStats: "Fehler beim Laden der Statistiken",
  medxErrLoadTerminologies: "Fehler beim Laden der Terminologien",
  medxErrCreate: "Fehler beim Erstellen des Medikaments",
  medxErrUpdate: "Fehler beim Aktualisieren des Medikaments",
  medxErrDelete: "Fehler beim Löschen des Medikaments",
};

const fr: typeof en = {
  medxFormTitleEdit: "Modifier le médicament",
  medxFormTitleNew: "Nouveau médicament",
  medxFormClose: "Fermer",
  medxFormSectionInfo: "Informations sur le médicament",
  medxFormName: "Nom du médicament (nom commercial)",
  medxFormNamePlaceholder: "p. ex. Doliprane",
  medxFormIngredient: "Principe actif",
  medxFormIngredientPlaceholder: "p. ex. Paracétamol",
  medxFormManufacturer: "Fabricant",
  medxFormManufacturerPlaceholder: "p. ex. Sanofi",
  medxFormPackageSize: "Taille du conditionnement",
  medxFormSelect: "Sélectionner...",
  medxFormDoseForm: "Forme galénique",
  medxFormPurpose: "Motif de la prescription",
  medxFormStatus: "Statut",
  medxFormSectionDosage: "Posologie",
  medxFormDose: "Dose",
  medxFormAmount: "Quantité",
  medxFormUnit: "Unité",
  medxFormSelectUnit: "Choisir une unité...",
  medxFormDosePreview: "Dose : {dose}",
  medxFormIntakeTimes: "Moments de prise",
  medxFormInstructions: "Consignes de prise",
  medxFormInstructionsPlaceholder: "p. ex. Après les repas avec beaucoup d’eau",
  medxFormStartDate: "Date de début",
  medxFormEndDate: "Date de fin",
  medxFormSectionPrescription: "Prescription",
  medxFormPrescriber: "Nom du prescripteur",
  medxFormPrescriberPlaceholder: "p. ex. Dr Martin",
  medxFormCountry: "Pays de prescription",
  medxFormSelectCountry: "Choisir un pays...",
  medxFormReason: "Motif de la prescription",
  medxFormReasonPlaceholder: "p. ex. Traitement aigu – maux de tête",
  medxFormPrescriptionScan: "Ordonnance numérique ou scan",
  medxFormUploadPrescription: "Téléverser l’ordonnance",
  medxFormPrescriptionUploaded: "Ordonnance téléversée",
  medxFormPrescriptionHint: "PDF ou image (5 Mo max.)",
  medxFormNoteTitle: "Remarque :",
  medxFormSelfSignedNote:
    "Lorsque vous saisissez vous-même un médicament, il est automatiquement marqué « Auto-signé » et associé à votre compte voyageur.",
  medxFormSectionImages: "Images du médicament",
  medxFormImagesHint:
    "Téléversez des images de la boîte et des comprimés (facultatif)",
  medxFormPackageImage: "Image de la boîte",
  medxFormPillImage: "Image du médicament/comprimé",
  medxFormPackagePreviewAlt: "Aperçu de la boîte",
  medxFormPillPreviewAlt: "Aperçu du médicament",
  medxFormRemoveImage: "Supprimer l’image",
  medxFormUploadPackage: "Téléverser l’image de la boîte",
  medxFormUploadPill: "Téléverser l’image du médicament",
  medxFormImageFormats: "JPG, PNG (10 Mo max.)",
  medxFormCaptureCamera: "Prendre avec l’appareil photo",
  medxFormImagesTip:
    "Des images de bonne qualité facilitent l’identification et peuvent être utiles pour les documents de voyage.",
  medxFormSaving: "Enregistrement...",
  medxFormSaveChanges: "Enregistrer les modifications",
  medxFormSaveNew: "Enregistrer le médicament",
  medxFormFhirId: "ID FHIR du médicament :",
  medxFormCapturePackageTitle: "Photographier la boîte",
  medxFormCapturePillTitle: "Photographier le médicament",
  medxFormConfirmDeletePackage:
    "Voulez-vous vraiment supprimer l’image de la boîte ?",
  medxFormConfirmDeletePill:
    "Voulez-vous vraiment supprimer l’image du médicament ?",
  medxFormPackageDeleted: "Image de la boîte supprimée",
  medxFormPillDeleted: "Image du médicament supprimée",
  medxFormDeleteFailed: "Échec de la suppression : {error}",
  medxFormPackageUploadFailed:
    "Le médicament a été enregistré, mais le téléversement de l’image de la boîte a échoué.\nErreur : {error}",
  medxFormPillUploadFailed:
    "Le médicament a été enregistré, mais le téléversement de l’image du médicament a échoué.\nErreur : {error}",
  medxFormSavedWithImages: "Médicament et images enregistrés avec succès !",
  medxFormSaveFailed: "Impossible d’enregistrer le médicament",
  medxFormUnknownError: "Erreur inconnue",

  medxDoseFormTablet: "Comprimé",
  medxDoseFormCapsule: "Gélule",
  medxDoseFormSyrup: "Sirop",
  medxDoseFormLiquid: "Liquide",
  medxDoseFormCream: "Crème",
  medxDoseFormInjection: "Injection",
  medxDoseFormInfusion: "Perfusion",
  medxDoseFormSuppository: "Suppositoire",
  medxDoseFormInhaler: "Inhalateur",

  medxPackageSizeSample: "Échantillon",
  medxPackageSizeCustom: "Personnalisé",

  medxStatusActive: "Actif",
  medxStatusCompleted: "Terminé",
  medxStatusStopped: "Arrêté",
  medxStatusDraft: "Brouillon",
  medxStatusOnHold: "En pause",
  medxStatusEnteredInError: "Saisie erronée",
  medxStatusIntended: "Prévu",
  medxStatusNotTaken: "Non pris",
  medxStatusUnknown: "Inconnu",

  medxUnitGroupWeight: "Poids",
  medxUnitGroupVolume: "Volume",
  medxUnitGroupCount: "Nombre",
  medxUnitGroupUnits: "Unités",
  medxUnitGroupPercentage: "Pourcentage",
  medxUnitMg: "milligrammes",
  medxUnitG: "grammes",
  medxUnitKg: "kilogrammes",
  medxUnitMl: "millilitres",
  medxUnitL: "litres",
  medxUnitMcg: "microgrammes",
  medxUnitIu: "unités internationales",
  medxUnitTablet: "Comprimé(s)",
  medxUnitCapsule: "Gélule(s)",
  medxUnitDrops: "Gouttes",
  medxUnitPiece: "Pièce(s)",
  medxDoseInputPlaceholder: "p. ex. 500",

  medxTimingMorning: "Chaque matin",
  medxTimingNoon: "Chaque midi",
  medxTimingAfternoon: "Chaque après-midi",
  medxTimingEvening: "Chaque soir",
  medxTimingNight: "Chaque nuit",
  medxTimingBedtime: "Au coucher",

  medxCamAccessDenied:
    "Accès à la caméra refusé. Veuillez vérifier les autorisations dans les paramètres du navigateur.",
  medxCamUnavailable: "Caméra indisponible",
  medxCamCapturedAlt: "Photo prise",
  medxCamRetake: "Reprendre",
  medxCamUsePhoto: "Utiliser la photo",
  medxCamSwitch: "Changer de caméra",
  medxCamTake: "Prendre une photo",
  medxCamClose: "Fermer la caméra",

  medxCardExpiringSoon: "Expire bientôt",
  medxCardExpired: "Expiré",
  medxCardCategory: "Catégorie",
  medxCardDosage: "Posologie",
  medxCardFrequency: "Fréquence",
  medxCardStartDate: "Date de début",
  medxCardInstructions: "Consignes :",
  medxCardBothPhotographed: "Boîte et médicament photographiés",
  medxCardPackagePhotographed: "Boîte photographiée",
  medxCardPillPhotographed: "Médicament photographié",
  medxCardEdit: "Modifier le médicament",
  medxCardDelete: "Supprimer le médicament",
  medxCardPackageAlt: "Boîte du médicament",
  medxCardPillAlt: "Médicament",

  medxPageLoading: "Chargement des médicaments...",
  medxPageSubtitle: "Gérez vos médicaments et ordonnances",
  medxPageTotal: "Total des médicaments",
  medxPageActive: "Actifs",
  medxPageExpiringSoon: "Expirent bientôt",
  medxPageExpired: "Expirés",
  medxPageSearch: "Rechercher des médicaments...",
  medxPageAllStatuses: "Tous les statuts",
  medxPageAllCategories: "Toutes les catégories",
  medxPageListTitle: "Médicaments ({count})",
  medxPageEmpty: "Aucun médicament trouvé",
  medxPageEmptyFiltered: "Essayez de modifier vos filtres",
  medxPageEmptyStart: "Ajoutez votre premier médicament pour commencer",
  medxPageAddFirst: "Ajouter votre premier médicament",
  medxPageConfirmDelete: "Voulez-vous vraiment supprimer ce médicament ?",
  medxPageDeleted: "Médicament supprimé",

  medxBack: "Retour aux médicaments",
  medxSavedToast: "Médicament enregistré avec succès !",
  medxUpdatedToast: "Médicament mis à jour avec succès !",

  medxListSubtitle: "Gérez vos médicaments de voyage",
  medxListTotal: "Total",
  medxListCompleted: "Terminés",
  medxListWithPrescription: "Avec ordonnance",
  medxListMine: "Mes médicaments",
  medxListEmpty: "Aucun médicament pour l’instant",
  medxListEmptyHint: "Ajoutez votre premier médicament",
  medxListManufacturer: "Fabricant : {name}",
  medxListStart: "Début : {date}",
  medxListPrescriptionFrom: "Prescrit en/au : {country}",

  medxModalDosagePlaceholder: "p. ex. 400 mg",
  medxModalFrequencyPlaceholder: "p. ex. 2 fois par jour",
  medxModalType: "Type de médicament",
  medxModalTravelCategory: "Catégorie de voyage",
  medxModalExtendedTitle: "Données étendues FHIR R5",
  medxModalTradeNamePlaceholder: "p. ex. Doliprane, Advil",
  medxModalTradeNameHint: "Nom commercial / de marque",
  medxModalIngredientPlaceholder: "p. ex. Paracétamol, Ibuprofène",
  medxModalIngredientHint: "Principe actif (DCI)",
  medxModalManufacturerPlaceholder: "p. ex. Sanofi, Bayer",
  medxModalManufacturerHint: "Fabricant du médicament",
  medxModalCodePlaceholder: "p. ex. 12345678 ou N02BA01",
  medxModalCodeHint: "Code produit international",
  medxModalBasicData: "Informations de base",
  medxModalPeriod: "Période",
  medxModalMedicalInfo: "Informations médicales",
  medxModalCategory: "Catégorie",
  medxModalPackagePhoto: "Photo de la boîte",
  medxModalPackagePhotoHint: "Photo de la boîte du médicament",
  medxModalPillPhoto: "Photo du comprimé",
  medxModalPillPhotoHint: "Photo du comprimé/de la pilule",

  medxErrLoad: "Impossible de charger les médicaments",
  medxErrLoadActive: "Impossible de charger les médicaments actifs",
  medxErrLoadByCategory: "Impossible de charger les médicaments par catégorie",
  medxErrLoadExpiring: "Impossible de charger les médicaments qui expirent",
  medxErrLoadOne: "Impossible de charger le médicament",
  medxErrLoadStats: "Impossible de charger les statistiques",
  medxErrLoadTerminologies: "Impossible de charger les terminologies",
  medxErrCreate: "Impossible de créer le médicament",
  medxErrUpdate: "Impossible de mettre à jour le médicament",
  medxErrDelete: "Impossible de supprimer le médicament",
};

const es: typeof en = {
  medxFormTitleEdit: "Editar medicamento",
  medxFormTitleNew: "Nuevo medicamento",
  medxFormClose: "Cerrar",
  medxFormSectionInfo: "Información del medicamento",
  medxFormName: "Nombre del medicamento (nombre comercial)",
  medxFormNamePlaceholder: "p. ej., Gelocatil",
  medxFormIngredient: "Principio activo",
  medxFormIngredientPlaceholder: "p. ej., Paracetamol",
  medxFormManufacturer: "Fabricante",
  medxFormManufacturerPlaceholder: "p. ej., Bayer",
  medxFormPackageSize: "Tamaño del envase",
  medxFormSelect: "Seleccionar...",
  medxFormDoseForm: "Forma farmacéutica",
  medxFormPurpose: "Motivo de la prescripción",
  medxFormStatus: "Estado",
  medxFormSectionDosage: "Posología",
  medxFormDose: "Dosis",
  medxFormAmount: "Cantidad",
  medxFormUnit: "Unidad",
  medxFormSelectUnit: "Seleccionar unidad...",
  medxFormDosePreview: "Dosis: {dose}",
  medxFormIntakeTimes: "Momentos de toma",
  medxFormInstructions: "Instrucciones de toma",
  medxFormInstructionsPlaceholder:
    "p. ej., Después de las comidas con abundante agua",
  medxFormStartDate: "Fecha de inicio",
  medxFormEndDate: "Fecha de fin",
  medxFormSectionPrescription: "Receta",
  medxFormPrescriber: "Nombre del prescriptor",
  medxFormPrescriberPlaceholder: "p. ej., Dra. García",
  medxFormCountry: "País de prescripción",
  medxFormSelectCountry: "Seleccionar un país...",
  medxFormReason: "Motivo de la prescripción",
  medxFormReasonPlaceholder: "p. ej., Tratamiento agudo – dolor de cabeza",
  medxFormPrescriptionScan: "Receta digital o escaneada",
  medxFormUploadPrescription: "Subir receta",
  medxFormPrescriptionUploaded: "Receta subida",
  medxFormPrescriptionHint: "PDF o imagen (máx. 5 MB)",
  medxFormNoteTitle: "Nota:",
  medxFormSelfSignedNote:
    "Cuando introduce usted mismo un medicamento, se marca automáticamente como «Autofirmado» y se vincula a su cuenta de viajero.",
  medxFormSectionImages: "Imágenes del medicamento",
  medxFormImagesHint:
    "Suba imágenes del envase y de las pastillas del medicamento (opcional)",
  medxFormPackageImage: "Imagen del envase",
  medxFormPillImage: "Imagen del medicamento/pastilla",
  medxFormPackagePreviewAlt: "Vista previa del envase",
  medxFormPillPreviewAlt: "Vista previa del medicamento",
  medxFormRemoveImage: "Eliminar imagen",
  medxFormUploadPackage: "Subir imagen del envase",
  medxFormUploadPill: "Subir imagen del medicamento",
  medxFormImageFormats: "JPG, PNG (máx. 10 MB)",
  medxFormCaptureCamera: "Capturar con la cámara",
  medxFormImagesTip:
    "Las imágenes de buena calidad facilitan la identificación y pueden ser útiles como documentación de viaje.",
  medxFormSaving: "Guardando...",
  medxFormSaveChanges: "Guardar cambios",
  medxFormSaveNew: "Guardar medicamento",
  medxFormFhirId: "ID FHIR del medicamento:",
  medxFormCapturePackageTitle: "Capturar imagen del envase",
  medxFormCapturePillTitle: "Capturar imagen del medicamento",
  medxFormConfirmDeletePackage:
    "¿Seguro que desea eliminar la imagen del envase?",
  medxFormConfirmDeletePill:
    "¿Seguro que desea eliminar la imagen del medicamento?",
  medxFormPackageDeleted: "Imagen del envase eliminada",
  medxFormPillDeleted: "Imagen del medicamento eliminada",
  medxFormDeleteFailed: "Error al eliminar: {error}",
  medxFormPackageUploadFailed:
    "El medicamento se guardó, pero la subida de la imagen del envase falló.\nError: {error}",
  medxFormPillUploadFailed:
    "El medicamento se guardó, pero la subida de la imagen del medicamento falló.\nError: {error}",
  medxFormSavedWithImages: "¡Medicamento e imágenes guardados correctamente!",
  medxFormSaveFailed: "No se pudo guardar el medicamento",
  medxFormUnknownError: "Error desconocido",

  medxDoseFormTablet: "Comprimido",
  medxDoseFormCapsule: "Cápsula",
  medxDoseFormSyrup: "Jarabe",
  medxDoseFormLiquid: "Líquido",
  medxDoseFormCream: "Crema",
  medxDoseFormInjection: "Inyección",
  medxDoseFormInfusion: "Infusión",
  medxDoseFormSuppository: "Supositorio",
  medxDoseFormInhaler: "Inhalador",

  medxPackageSizeSample: "Muestra",
  medxPackageSizeCustom: "Personalizado",

  medxStatusActive: "Activo",
  medxStatusCompleted: "Completado",
  medxStatusStopped: "Suspendido",
  medxStatusDraft: "Borrador",
  medxStatusOnHold: "En pausa",
  medxStatusEnteredInError: "Introducido por error",
  medxStatusIntended: "Previsto",
  medxStatusNotTaken: "No tomado",
  medxStatusUnknown: "Desconocido",

  medxUnitGroupWeight: "Peso",
  medxUnitGroupVolume: "Volumen",
  medxUnitGroupCount: "Cantidad",
  medxUnitGroupUnits: "Unidades",
  medxUnitGroupPercentage: "Porcentaje",
  medxUnitMg: "miligramos",
  medxUnitG: "gramos",
  medxUnitKg: "kilogramos",
  medxUnitMl: "mililitros",
  medxUnitL: "litros",
  medxUnitMcg: "microgramos",
  medxUnitIu: "unidades internacionales",
  medxUnitTablet: "Comprimido(s)",
  medxUnitCapsule: "Cápsula(s)",
  medxUnitDrops: "Gotas",
  medxUnitPiece: "Unidad(es)",
  medxDoseInputPlaceholder: "p. ej., 500",

  medxTimingMorning: "Cada mañana",
  medxTimingNoon: "Cada mediodía",
  medxTimingAfternoon: "Cada tarde",
  medxTimingEvening: "Cada noche (al anochecer)",
  medxTimingNight: "Cada noche",
  medxTimingBedtime: "Al acostarse",

  medxCamAccessDenied:
    "Acceso a la cámara denegado. Compruebe los permisos en la configuración del navegador.",
  medxCamUnavailable: "Cámara no disponible",
  medxCamCapturedAlt: "Foto capturada",
  medxCamRetake: "Repetir",
  medxCamUsePhoto: "Usar foto",
  medxCamSwitch: "Cambiar cámara",
  medxCamTake: "Tomar foto",
  medxCamClose: "Cerrar cámara",

  medxCardExpiringSoon: "Caduca pronto",
  medxCardExpired: "Caducado",
  medxCardCategory: "Categoría",
  medxCardDosage: "Posología",
  medxCardFrequency: "Frecuencia",
  medxCardStartDate: "Fecha de inicio",
  medxCardInstructions: "Instrucciones:",
  medxCardBothPhotographed: "Envase y medicamento fotografiados",
  medxCardPackagePhotographed: "Envase fotografiado",
  medxCardPillPhotographed: "Medicamento fotografiado",
  medxCardEdit: "Editar medicamento",
  medxCardDelete: "Eliminar medicamento",
  medxCardPackageAlt: "Envase del medicamento",
  medxCardPillAlt: "Medicamento",

  medxPageLoading: "Cargando medicamentos...",
  medxPageSubtitle: "Gestione sus medicamentos y recetas",
  medxPageTotal: "Total de medicamentos",
  medxPageActive: "Activos",
  medxPageExpiringSoon: "Caducan pronto",
  medxPageExpired: "Caducados",
  medxPageSearch: "Buscar medicamentos...",
  medxPageAllStatuses: "Todos los estados",
  medxPageAllCategories: "Todas las categorías",
  medxPageListTitle: "Medicamentos ({count})",
  medxPageEmpty: "No se encontraron medicamentos",
  medxPageEmptyFiltered: "Pruebe a ajustar los filtros",
  medxPageEmptyStart: "Añada su primer medicamento para empezar",
  medxPageAddFirst: "Añadir su primer medicamento",
  medxPageConfirmDelete: "¿Seguro que desea eliminar este medicamento?",
  medxPageDeleted: "Medicamento eliminado",

  medxBack: "Volver a los medicamentos",
  medxSavedToast: "¡Medicamento guardado correctamente!",
  medxUpdatedToast: "¡Medicamento actualizado correctamente!",

  medxListSubtitle: "Gestione sus medicamentos de viaje",
  medxListTotal: "Total",
  medxListCompleted: "Completados",
  medxListWithPrescription: "Con receta",
  medxListMine: "Mis medicamentos",
  medxListEmpty: "Todavía no hay medicamentos",
  medxListEmptyHint: "Añada su primer medicamento",
  medxListManufacturer: "Fabricante: {name}",
  medxListStart: "Inicio: {date}",
  medxListPrescriptionFrom: "Recetado en: {country}",

  medxModalDosagePlaceholder: "p. ej., 400 mg",
  medxModalFrequencyPlaceholder: "p. ej., 2 veces al día",
  medxModalType: "Tipo de medicamento",
  medxModalTravelCategory: "Categoría de viaje",
  medxModalExtendedTitle: "Datos ampliados FHIR R5",
  medxModalTradeNamePlaceholder: "p. ej., Gelocatil, Ibuprofeno Cinfa",
  medxModalTradeNameHint: "Nombre comercial o de marca",
  medxModalIngredientPlaceholder: "p. ej., Paracetamol, Ibuprofeno",
  medxModalIngredientHint: "Principio activo (DCI)",
  medxModalManufacturerPlaceholder: "p. ej., Bayer, Cinfa",
  medxModalManufacturerHint: "Fabricante del medicamento",
  medxModalCodePlaceholder: "p. ej., 12345678 o N02BA01",
  medxModalCodeHint: "Código internacional del producto",
  medxModalBasicData: "Datos básicos",
  medxModalPeriod: "Periodo",
  medxModalMedicalInfo: "Información médica",
  medxModalCategory: "Categoría",
  medxModalPackagePhoto: "Foto del envase",
  medxModalPackagePhotoHint: "Foto del envase del medicamento",
  medxModalPillPhoto: "Foto del comprimido/pastilla",
  medxModalPillPhotoHint: "Foto del comprimido/la pastilla",

  medxErrLoad: "Error al cargar los medicamentos",
  medxErrLoadActive: "Error al cargar los medicamentos activos",
  medxErrLoadByCategory: "Error al cargar los medicamentos por categoría",
  medxErrLoadExpiring: "Error al cargar los medicamentos que caducan",
  medxErrLoadOne: "Error al cargar el medicamento",
  medxErrLoadStats: "Error al cargar las estadísticas",
  medxErrLoadTerminologies: "Error al cargar las terminologías",
  medxErrCreate: "Error al crear el medicamento",
  medxErrUpdate: "Error al actualizar el medicamento",
  medxErrDelete: "Error al eliminar el medicamento",
};

const pt: typeof en = {
  medxFormTitleEdit: "Editar medicamento",
  medxFormTitleNew: "Novo medicamento",
  medxFormClose: "Fechar",
  medxFormSectionInfo: "Informações do medicamento",
  medxFormName: "Nome do medicamento (nome comercial)",
  medxFormNamePlaceholder: "p. ex. Ben-u-ron",
  medxFormIngredient: "Substância ativa",
  medxFormIngredientPlaceholder: "p. ex. Paracetamol",
  medxFormManufacturer: "Fabricante",
  medxFormManufacturerPlaceholder: "p. ex. Bayer",
  medxFormPackageSize: "Tamanho da embalagem",
  medxFormSelect: "Selecionar...",
  medxFormDoseForm: "Forma farmacêutica",
  medxFormPurpose: "Finalidade da prescrição",
  medxFormStatus: "Estado",
  medxFormSectionDosage: "Posologia",
  medxFormDose: "Dose",
  medxFormAmount: "Quantidade",
  medxFormUnit: "Unidade",
  medxFormSelectUnit: "Selecionar unidade...",
  medxFormDosePreview: "Dose: {dose}",
  medxFormIntakeTimes: "Horários de toma",
  medxFormInstructions: "Instruções de toma",
  medxFormInstructionsPlaceholder: "p. ex. Após as refeições com bastante água",
  medxFormStartDate: "Data de início",
  medxFormEndDate: "Data de fim",
  medxFormSectionPrescription: "Receita",
  medxFormPrescriber: "Nome do prescritor",
  medxFormPrescriberPlaceholder: "p. ex. Dr. Silva",
  medxFormCountry: "País da prescrição",
  medxFormSelectCountry: "Selecionar um país...",
  medxFormReason: "Motivo da prescrição",
  medxFormReasonPlaceholder: "p. ex. Tratamento agudo – dor de cabeça",
  medxFormPrescriptionScan: "Receita digital ou digitalização",
  medxFormUploadPrescription: "Carregar receita",
  medxFormPrescriptionUploaded: "Receita carregada",
  medxFormPrescriptionHint: "PDF ou imagem (máx. 5 MB)",
  medxFormNoteTitle: "Nota:",
  medxFormSelfSignedNote:
    "Quando introduz um medicamento por si próprio, este é automaticamente marcado como «Autoassinado» e associado à sua conta de viajante.",
  medxFormSectionImages: "Imagens do medicamento",
  medxFormImagesHint:
    "Carregue imagens da embalagem e dos comprimidos do medicamento (opcional)",
  medxFormPackageImage: "Imagem da embalagem",
  medxFormPillImage: "Imagem do medicamento/comprimido",
  medxFormPackagePreviewAlt: "Pré-visualização da embalagem",
  medxFormPillPreviewAlt: "Pré-visualização do medicamento",
  medxFormRemoveImage: "Remover imagem",
  medxFormUploadPackage: "Carregar imagem da embalagem",
  medxFormUploadPill: "Carregar imagem do medicamento",
  medxFormImageFormats: "JPG, PNG (máx. 10 MB)",
  medxFormCaptureCamera: "Capturar com a câmara",
  medxFormImagesTip:
    "Imagens de boa qualidade ajudam na identificação e podem ser úteis como documentação de viagem.",
  medxFormSaving: "A guardar...",
  medxFormSaveChanges: "Guardar alterações",
  medxFormSaveNew: "Guardar medicamento",
  medxFormFhirId: "ID FHIR do medicamento:",
  medxFormCapturePackageTitle: "Capturar imagem da embalagem",
  medxFormCapturePillTitle: "Capturar imagem do medicamento",
  medxFormConfirmDeletePackage:
    "Tem a certeza de que pretende eliminar a imagem da embalagem?",
  medxFormConfirmDeletePill:
    "Tem a certeza de que pretende eliminar a imagem do medicamento?",
  medxFormPackageDeleted: "Imagem da embalagem eliminada",
  medxFormPillDeleted: "Imagem do medicamento eliminada",
  medxFormDeleteFailed: "Falha ao eliminar: {error}",
  medxFormPackageUploadFailed:
    "O medicamento foi guardado, mas o carregamento da imagem da embalagem falhou.\nErro: {error}",
  medxFormPillUploadFailed:
    "O medicamento foi guardado, mas o carregamento da imagem do medicamento falhou.\nErro: {error}",
  medxFormSavedWithImages: "Medicamento e imagens guardados com sucesso!",
  medxFormSaveFailed: "Não foi possível guardar o medicamento",
  medxFormUnknownError: "Erro desconhecido",

  medxDoseFormTablet: "Comprimido",
  medxDoseFormCapsule: "Cápsula",
  medxDoseFormSyrup: "Xarope",
  medxDoseFormLiquid: "Líquido",
  medxDoseFormCream: "Creme",
  medxDoseFormInjection: "Injeção",
  medxDoseFormInfusion: "Perfusão",
  medxDoseFormSuppository: "Supositório",
  medxDoseFormInhaler: "Inalador",

  medxPackageSizeSample: "Amostra",
  medxPackageSizeCustom: "Personalizado",

  medxStatusActive: "Ativo",
  medxStatusCompleted: "Concluído",
  medxStatusStopped: "Interrompido",
  medxStatusDraft: "Rascunho",
  medxStatusOnHold: "Em pausa",
  medxStatusEnteredInError: "Introduzido por erro",
  medxStatusIntended: "Previsto",
  medxStatusNotTaken: "Não tomado",
  medxStatusUnknown: "Desconhecido",

  medxUnitGroupWeight: "Peso",
  medxUnitGroupVolume: "Volume",
  medxUnitGroupCount: "Quantidade",
  medxUnitGroupUnits: "Unidades",
  medxUnitGroupPercentage: "Percentagem",
  medxUnitMg: "miligramas",
  medxUnitG: "gramas",
  medxUnitKg: "quilogramas",
  medxUnitMl: "mililitros",
  medxUnitL: "litros",
  medxUnitMcg: "microgramas",
  medxUnitIu: "unidades internacionais",
  medxUnitTablet: "Comprimido(s)",
  medxUnitCapsule: "Cápsula(s)",
  medxUnitDrops: "Gotas",
  medxUnitPiece: "Unidade(s)",
  medxDoseInputPlaceholder: "p. ex. 500",

  medxTimingMorning: "Todas as manhãs",
  medxTimingNoon: "Todos os dias ao meio-dia",
  medxTimingAfternoon: "Todas as tardes",
  medxTimingEvening: "Todas as noites (ao fim do dia)",
  medxTimingNight: "Todas as noites",
  medxTimingBedtime: "Ao deitar",

  medxCamAccessDenied:
    "Acesso à câmara recusado. Verifique as permissões nas definições do navegador.",
  medxCamUnavailable: "Câmara indisponível",
  medxCamCapturedAlt: "Fotografia capturada",
  medxCamRetake: "Repetir",
  medxCamUsePhoto: "Usar fotografia",
  medxCamSwitch: "Trocar câmara",
  medxCamTake: "Tirar fotografia",
  medxCamClose: "Fechar câmara",

  medxCardExpiringSoon: "Expira em breve",
  medxCardExpired: "Expirado",
  medxCardCategory: "Categoria",
  medxCardDosage: "Posologia",
  medxCardFrequency: "Frequência",
  medxCardStartDate: "Data de início",
  medxCardInstructions: "Instruções:",
  medxCardBothPhotographed: "Embalagem e medicamento fotografados",
  medxCardPackagePhotographed: "Embalagem fotografada",
  medxCardPillPhotographed: "Medicamento fotografado",
  medxCardEdit: "Editar medicamento",
  medxCardDelete: "Eliminar medicamento",
  medxCardPackageAlt: "Embalagem do medicamento",
  medxCardPillAlt: "Medicamento",

  medxPageLoading: "A carregar medicamentos...",
  medxPageSubtitle: "Gira os seus medicamentos e receitas",
  medxPageTotal: "Total de medicamentos",
  medxPageActive: "Ativos",
  medxPageExpiringSoon: "Expiram em breve",
  medxPageExpired: "Expirados",
  medxPageSearch: "Pesquisar medicamentos...",
  medxPageAllStatuses: "Todos os estados",
  medxPageAllCategories: "Todas as categorias",
  medxPageListTitle: "Medicamentos ({count})",
  medxPageEmpty: "Nenhum medicamento encontrado",
  medxPageEmptyFiltered: "Experimente ajustar os filtros",
  medxPageEmptyStart: "Adicione o seu primeiro medicamento para começar",
  medxPageAddFirst: "Adicionar o primeiro medicamento",
  medxPageConfirmDelete:
    "Tem a certeza de que pretende eliminar este medicamento?",
  medxPageDeleted: "Medicamento eliminado",

  medxBack: "Voltar aos medicamentos",
  medxSavedToast: "Medicamento guardado com sucesso!",
  medxUpdatedToast: "Medicamento atualizado com sucesso!",

  medxListSubtitle: "Gira os seus medicamentos de viagem",
  medxListTotal: "Total",
  medxListCompleted: "Concluídos",
  medxListWithPrescription: "Com receita",
  medxListMine: "Os meus medicamentos",
  medxListEmpty: "Ainda não há medicamentos",
  medxListEmptyHint: "Adicione o seu primeiro medicamento",
  medxListManufacturer: "Fabricante: {name}",
  medxListStart: "Início: {date}",
  medxListPrescriptionFrom: "Receitado em: {country}",

  medxModalDosagePlaceholder: "p. ex. 400 mg",
  medxModalFrequencyPlaceholder: "p. ex. 2 vezes por dia",
  medxModalType: "Tipo de medicamento",
  medxModalTravelCategory: "Categoria de viagem",
  medxModalExtendedTitle: "Dados alargados FHIR R5",
  medxModalTradeNamePlaceholder: "p. ex. Ben-u-ron, Brufen",
  medxModalTradeNameHint: "Nome comercial ou de marca",
  medxModalIngredientPlaceholder: "p. ex. Paracetamol, Ibuprofeno",
  medxModalIngredientHint: "Substância ativa (DCI)",
  medxModalManufacturerPlaceholder: "p. ex. Bayer, Bial",
  medxModalManufacturerHint: "Fabricante do medicamento",
  medxModalCodePlaceholder: "p. ex. 12345678 ou N02BA01",
  medxModalCodeHint: "Código internacional do produto",
  medxModalBasicData: "Dados básicos",
  medxModalPeriod: "Período",
  medxModalMedicalInfo: "Informações médicas",
  medxModalCategory: "Categoria",
  medxModalPackagePhoto: "Fotografia da embalagem",
  medxModalPackagePhotoHint: "Fotografia da embalagem do medicamento",
  medxModalPillPhoto: "Fotografia do comprimido",
  medxModalPillPhotoHint: "Fotografia do comprimido/da pílula",

  medxErrLoad: "Erro ao carregar os medicamentos",
  medxErrLoadActive: "Erro ao carregar os medicamentos ativos",
  medxErrLoadByCategory: "Erro ao carregar os medicamentos por categoria",
  medxErrLoadExpiring: "Erro ao carregar os medicamentos a expirar",
  medxErrLoadOne: "Erro ao carregar o medicamento",
  medxErrLoadStats: "Erro ao carregar as estatísticas",
  medxErrLoadTerminologies: "Erro ao carregar as terminologias",
  medxErrCreate: "Erro ao criar o medicamento",
  medxErrUpdate: "Erro ao atualizar o medicamento",
  medxErrDelete: "Erro ao eliminar o medicamento",
};

export const medicationsStrings: Record<Language, typeof en> = {
  en,
  de,
  fr,
  es,
  pt,
};
